import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { arrayOf, bool, func, shape } from 'prop-types'
import { useCallback, useState, type VFC } from 'react'

import { PAGE_TYPE_SEARCH_RESULTS } from 'shared/consts'
import { TYPE_SERP_LOAD_MORE } from 'shared/experiments/components/SerpLoadMore/consts'
import { EXPERIMENT_VARIATION, SERP_LOAD_MORE } from 'shared/experiments/consts'
import { event as e } from 'shared/experiments/utils/event'
import trackEvent from 'shared/experiments/utils/trackOptimizelyEvent'
import store from 'shared/store'
import noop from 'shared/utils/noop'
import { type InferPropsWithDefaults } from 'shared/utils/propTypes'
import { type PublicRuntimeConfig } from 'types/shopConfig'
import Button from 'views/components/atoms/Button'
import Experiment from 'views/components/utils/optimizely/Experiment'
import { hitsType } from 'views/utils/propTypes/hits'

import ProgressSpinner from './ProgressSpinner'

import './SerpLoadMore.scss'

const LoadMore: VFC<ComponentProps> = ({
  hits,
  hasMore,
  refineNext,
  publicRuntimeConfig,
}) => {
  const {
    pageProperties: { pageType },
  } = publicRuntimeConfig

  const [prevLength, setPrevLength] = useState(hits.length)
  const [isLoading, setIsLoading] = useState(false)

  const handleLoadMore = useCallback(() => {
    if (hasMore && typeof refineNext === 'function') {
      setIsLoading(true)
      store.dispatch({ type: TYPE_SERP_LOAD_MORE, refineNext })
      trackEvent(e`Load more button click`)
    }
  }, [hasMore, refineNext])

  if (prevLength !== hits.length) {
    setIsLoading(false)
    setPrevLength(hits.length)
  }

  return (
    <Experiment name={SERP_LOAD_MORE}>
      {({ variant }) => {
        if (
          pageType !== PAGE_TYPE_SEARCH_RESULTS ||
          variant === EXPERIMENT_VARIATION.DEFAULT ||
          !hasMore
        ) {
          return null
        }

        return (
          <div className="u-text-align--center">
            <Button
              name="loadMoreResults"
              id="loadMoreResults"
              onClick={handleLoadMore}
              style="buttonGhost"
              className={classNames('e-SerpLoadMore__button', {
                'e-SerpLoadMore__button--loading': isLoading,
              })}
            >
              {isLoading ? (
                <ProgressSpinner />
              ) : (
                <Trans id="general.label.loadMoreProducts" />
              )}
            </Button>
          </div>
        )
      }}
    </Experiment>
  )
}

const LoadMorePropTypes = {
  hits: arrayOf(shape(hitsType)).isRequired,
  hasMore: bool,
  refineNext: func,
}

const LoadMoreDefaultProps = {
  hasMore: false,
  refineNext: noop,
}

type ComponentProps = InferPropsWithDefaults<
  typeof LoadMorePropTypes,
  typeof LoadMoreDefaultProps
> & {
  publicRuntimeConfig: PublicRuntimeConfig
}

export default LoadMore
